import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

/**
* Stimulus controller for saved form view modal (create/edit saved searches)
* Handles modal display, form validation, version conflict detection, and save operations
*
* Usage:
* <div data-controller="saved-form-view--modal"
*      data-saved-form-view--modal-url-value="/saved_searches"
*      data-saved-form-view--modal-context-value="portfolio">
*   <div data-saved-form-view--modal-target="modal" class="modal">
*     <input data-saved-form-view--modal-target="nameInput" type="text">
*     <button data-action="saved-form-view--modal#save">Save</button>
*   </div>
* </div>
*/
export default class extends Controller {
  static targets = ["modal", "nameInput", "descriptionInput", "form", "title", "saveButton", "versionInput"]
  static values = { 
    context: String,
    url: String,
    formSelector: String
  }

  connect() {
    if (this.hasModalTarget && !this.modal) {
      this.modal = new Modal(this.modalTarget)
    }
    this.currentSearchId = null
    document.addEventListener('saved-form-view:check-result', this.handleCheckResult.bind(this))
  }

  disconnect() {
    document.removeEventListener('saved-form-view:check-result', this.handleCheckResult.bind(this))

    if (this.modal) {
      this.modal.hide()
      this.modal.dispose()
      this.modal = null
    }
  }

  showCreateModal(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (!this.modal) return

    this.currentSearchId = null
    this.nameInputTarget.value = ''
    this.descriptionInputTarget.value = ''
    this.versionInputTarget.value = ''
    this.titleTarget.textContent = 'Save'
    this.saveButtonTarget.textContent = 'Save'
    this.modal.show()
  }

  showEditModal(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.modal) return

    const editingSearchId = event.currentTarget.dataset.searchId
    const editingSearchName = event.currentTarget.dataset.searchName
    const originalVersion = parseInt(event.currentTarget.dataset.searchVersion)

    this.pendingEditId = editingSearchId
    this.pendingEditName = editingSearchName
    this.originalVersion = originalVersion
    this.versionInputTarget.value = originalVersion

    document.dispatchEvent(new CustomEvent('saved-form-view:check-active', {
      detail: { editingSearchId, editingSearchName }
    }))
  }

  handleCheckResult(event) {
    const { confirmed, editingSearchId } = event.detail

    if (editingSearchId === this.pendingEditId) {
      if (confirmed) {
        this.proceedWithEdit()
      }
    }
  }

  proceedWithEdit() {
    if (!this.modal) return

    this.currentSearchId = this.pendingEditId

    this.titleTarget.textContent = 'Loading...'
    this.saveButtonTarget.textContent = 'Loading...'
    this.saveButtonTarget.disabled = true
    this.modal.show()

    fetch(`${this.urlValue}/${this.currentSearchId}`)
      .then(response => response.json())
      .then(data => {
        this.nameInputTarget.value = data.name
        this.descriptionInputTarget.value = data.description || ''
        this.versionInputTarget.value = data.version
        this.titleTarget.textContent = 'Edit'
        this.saveButtonTarget.textContent = 'Update'
        this.saveButtonTarget.disabled = false

        if (this.originalVersion && data.version > this.originalVersion) {
          this.showVersionWarning()
        }
      })
      .catch(error => {
        console.error('Failed to fetch record:', error)
        this.modal.hide()
      })
  }

  showVersionWarning() {
    const warningHtml = `
      <div class="alert alert-warning mb-3">
        <i class="fa fa-exclamation-triangle me-2"></i>
        <strong>Notice:</strong> This saved search was modified by someone else.
        Consider <a href="javascript:window.location.reload()" class="alert-link">reloading the page</a>
        to see the latest version.
      </div>
    `

    const modalBody = this.modalTarget.querySelector('.modal-body')
    modalBody.insertAdjacentHTML('afterbegin', warningHtml)
  }

  // Quick update without showing modal - just saves current params to existing view
  quickUpdate(event) {
    event.preventDefault()

    const button = event.currentTarget
    const searchId = button.dataset.searchId
    const searchName = button.dataset.searchName
    const searchVersion = button.dataset.searchVersion

    if (!searchId) {
      console.error('No search ID provided for quick update')
      return
    }

    // Disable button and create loading indicator
    button.disabled = true

    // Create loading indicator element
    const indicator = document.createElement('span')
    indicator.style.cssText = `
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      margin-left: 8px;
      transition: all 0.3s ease;
    `
    indicator.innerHTML = '<i class="fa fa-spinner fa-spin"></i>'

    // Insert indicator next to button
    button.parentNode.insertBefore(indicator, button.nextSibling)

    // Get current params from dropdowns
    const taskBetaController = this.application.getControllerForElementAndIdentifier(
      this.element,
      'task-beta-saved-views'
    )

    let searchParams = {}
    if (taskBetaController && taskBetaController.getCurrentParams) {
      searchParams = taskBetaController.getCurrentParams()
    }

    const data = {
      saved_form_view: {
        name: searchName,
        version: searchVersion,
        context: this.contextValue,
        search_params: searchParams
      }
    }

    const url = `${this.urlValue}/${searchId}`

    fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'text/vnd.turbo-stream.html'
      },
      body: JSON.stringify(data)
    })
    .then(response => {
      if (response.ok) {
        return response.text()
      } else {
        throw new Error('Update failed')
      }
    })
    .then(html => {
      Turbo.renderStreamMessage(html)

      // Show success indicator
      this.updateStatusIndicator(indicator, 'success')

      // Keep button disabled since changes are now saved
      button.disabled = true

      // Hide the "Undo Changes" link
      const revertLink = button.parentElement.querySelector('a[href*="revert=true"]')
      if (revertLink) {
        revertLink.style.display = 'none'
      }

      // Update URL to clean state (only view_id) without reloading
      const currentUrl = new URL(window.location.href)
      const cleanUrl = new URL(currentUrl.origin + currentUrl.pathname)
      cleanUrl.searchParams.set('view_id', searchId)
      window.history.replaceState({}, '', cleanUrl.toString())

      document.dispatchEvent(new CustomEvent('saved-form-view:updated', {
        detail: {
          searchId: searchId,
          searchName: searchName
        },
        bubbles: true
      }))
    })
    .catch(error => {
      console.error('Update error:', error)

      // Show error indicator
      this.updateStatusIndicator(indicator, 'error')

      // Re-enable button
      button.disabled = false
    })
  }

  updateStatusIndicator(indicator, status) {
    if (status === 'success') {
      indicator.className = 'bg-primary'
      indicator.innerHTML = '<i class="fa fa-check text-white fs-6"></i>'
    } else {
      indicator.className = 'bg-danger'
      indicator.innerHTML = '<i class="fa fa-times text-white fs-6"></i>'
    }

    // Fade out and remove indicator after 2 seconds
    setTimeout(() => {
      indicator.style.opacity = '0'
      setTimeout(() => {
        indicator.remove()
      }, 300)
    }, 2000)
  }

  save(event) {
    event.preventDefault() // NOTE: Using Turbo.renderStreamMessage() below

    // Try to get params from task-beta-saved-views controller first
    const taskBetaController = this.application.getControllerForElementAndIdentifier(
      this.element,
      'task-beta-saved-views'
    )

    let searchParams = {}
    if (taskBetaController && taskBetaController.getCurrentParams) {
      // Use My Work controller to gather params from all dropdowns
      searchParams = taskBetaController.getCurrentParams()
    } else {
      // Fallback to original behavior for other contexts
      const formElement = this.formSelectorValue ?
        document.querySelector(this.formSelectorValue) :
        document.querySelector('[data-controller*="saved-form-view--form"] form')

      if (formElement) {
        const formController = this.application.getControllerForElementAndIdentifier(
          formElement.closest('[data-controller*="saved-form-view--form"]'),
          'saved-form-view--form'
        )
        if (formController) {
          searchParams = formController.getCurrentParams()
        }
      }
    }

    const data = {
      saved_form_view: {
        name: this.nameInputTarget.value,
        description: this.descriptionInputTarget.value,
        version: this.versionInputTarget.value,
        context: this.contextValue,
        search_params: searchParams
      }
    }

    const url = this.currentSearchId ? 
      `${this.urlValue}/${this.currentSearchId}` : 
      this.urlValue
    
    const method = this.currentSearchId ? 'PATCH' : 'POST'

    fetch(url, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'text/vnd.turbo-stream.html'
      },
      body: JSON.stringify(data)
    })
    .then(response => {
      if (response.ok) {
        return response.text() // Get the HTML content
      } else {
        throw new Error('Save failed')
      }
    })
    .then(html => {
      // Process the turbo stream response
      Turbo.renderStreamMessage(html)
      this.modal.hide()

      document.dispatchEvent(new CustomEvent('saved-form-view:updated', {
        detail: {
          searchId: this.currentSearchId,
          searchName: this.nameInputTarget.value,
          searchDescription: this.descriptionInputTarget.value
        },
        bubbles: true
      }))

      // Clear any warnings from previous edits
      const warning = this.modalTarget.querySelector('.alert-warning')
      if (warning) warning.remove()
    })
    .catch(error => {
      console.error('Save error:', error)
    })
  }
}
